// src/components/pages/campaigns/campaigns-list.js
import { useState, useMemo, useEffect } from 'react';
import { Button, Intent, Icon, Alert } from '@blueprintjs/core';
import { useHistory } from 'react-router-dom';
import * as ROUTES from '../../constants/routes';
import * as CampaignModel from '../../models/campaign';
import { getCampaignStatus } from '../../utils/campaignHelpers';
import { Wrapper, ClickableTableRow, NoWrapSpan } from './campaigns-list.styles';
import { StyledPanelHeader, Row } from '../heatmap-page-components/styled';
import { AuraFilterMenu } from '../filter-menu';
import NoAreasPanel from '../no-areas-panel';
import { getStandardFormatDate } from '../../utils/dates';
import { formatNumbersWithUnits } from '../../utils/formatNumbers';
import MetricChangeComponent from '../campaign-manager-components/metric-change-component';
import {
  Table,
  TableWrapper,
  TableHeaders,
  TableHeading,
  TableRow,
  Cell
} from '../metrics-table/table';

type Props = {
  campaigns: CampaignModel.t[],
  onDeleteCampaign?: (id: string) => void,
};

const CampaignsList = (props: Props) => {
  const [campaigns, setCampaigns] = useState([]);
  const [ownerFilter, setOwnerFilter] = useState([]);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const history = useHistory();

  const handleDeleteConfirm = () => {
    if (campaignToDelete) {
      props.onDeleteCampaign(campaignToDelete);
      setCampaignToDelete(null);
    }
  }

  // Get unique owner names
  const ownerMap = useMemo(() => {
    const map = new Map();
    props.campaigns.forEach(c => {
      map.set(`${c.ownerId}-${c.ownerEmail}`, { id: c.ownerId, name: c.ownerEmail });
    });
    const mapResult = Array.from(map.values());
    setOwnerFilter(Array.from(mapResult.map(o => o.id)));
    return mapResult;
  }, [props.campaigns]);

  useEffect(() => {
    setCampaigns(props.campaigns
        .filter(c => ownerFilter.includes(c.ownerId))
        .sort((a, b) => new Date(b.testPeriod.start) - new Date(a.testPeriod.start))
    );
  }, [props.campaigns, ownerFilter]);

  return (
    <Wrapper>
      <StyledPanelHeader>
        <Row>
          <AuraFilterMenu
            type={'default'}
            allItems={ownerMap}
            selectedItems={ownerFilter}
            updateSelectedItems={val => {
              setOwnerFilter(val)
            }}
            noItemsText="No owners"
            itemsTypeText="owners"
            isLoading={false}
            canSelectAll={true}
            enabledItems={ownerMap.map(o => o.id)}
          />
          <Button
            intent={Intent.PRIMARY}
            text="New Campaign"
            icon="add"
            onClick={() => history.push(ROUTES.SINGLE_CAMPAIGN.replace(':campaignId', 'new'))}
          />
        </Row>
      </StyledPanelHeader>
      { !campaigns || !campaigns.length ?
          <Wrapper>
            <NoAreasPanel message="No campaigns to display" />
          </Wrapper>
        :
          <TableWrapper>
            <Table>
              <TableHeaders>
                <tr>
                  <TableHeading>Name</TableHeading>
                  <TableHeading>Owner Name</TableHeading>
                  {/* <TableHeading>Status</TableHeading> */}
                  <TableHeading>Start</TableHeading>
                  <TableHeading>Metric</TableHeading>
                  <TableHeading>Uplift</TableHeading>
                  <TableHeading>Action</TableHeading>
                </tr>
              </TableHeaders>
              <tbody>
                {campaigns.map((campaign) => (
                  <ClickableTableRow
                    key={campaign.id}
                    onClick={() => history.push(ROUTES.SINGLE_CAMPAIGN.replace(':campaignId', campaign.id))}
                  >
                    <Cell cellType={'string'}>{campaign.name}</Cell>
                    <Cell cellType={'string'}>{campaign.ownerName}</Cell>
                    {/* <Cell cellType={'string'}>
                      <NoWrapSpan>
                        <Icon icon={getCampaignStatus(campaign).icon} style={{ marginRight: 6 }} />
                        {getCampaignStatus(campaign).status}
                      </NoWrapSpan>
                    </Cell> */}
                    <Cell cellType={'string'}>{getStandardFormatDate(campaign.testPeriod.selectedDates.start)}</Cell>
                    <Cell cellType={'string'}>{campaign.metric.display}</Cell>
                    <Cell cellType={'number'}>
                      <MetricChangeComponent
                        value={campaign.campaignUplift}
                        unit={campaign.metric.unitType}
                      />
                    </Cell>
                    <Cell cellType={'string'} style={{ textAlign: 'center' }}>
                      <Icon
                        icon="trash"
                        intent={Intent.DANGER}
                        style={{ cursor: 'pointer' }}
                        onClick={e => {
                          e.stopPropagation();
                          setCampaignToDelete(campaign.id)
                        }}
                      />
                    </Cell>
                  </ClickableTableRow>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
      }
      <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete Campaign"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={!!campaignToDelete}
          onCancel={() => {
            setCampaignToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
        >
          <p>
            Are you sure you want to delete this campaign?
          </p>
        </Alert>
    </Wrapper>
  );
};

export default CampaignsList;